import { useTranslation } from 'react-i18next';

import { createFileRoute } from '@tanstack/react-router';
import { useAtom, useSetAtom } from 'jotai';
import { CreateProducts } from '~/components/common/setup/create-products/create-products';
import { Edit } from '~/components/common/setup/edit/edit';
import { EnrichmentProducts } from '~/components/common/setup/enrichment-products/enrichment-products';
import { crawlingProductsState } from '~/components/common/setup/states/crawlingProductState';
import { stepState } from '~/components/common/setup/states/stepState';
import {
  type CrawlingProduct,
  CrawlingProductStatus,
  Steps,
} from '~/components/common/setup/types';
import { UploadCSV } from '~/components/common/setup/upload-csv/upload-csv';
import { AlertCircleIcon } from '~/components/ui/icons/alert-circle';
import { Loader } from '~/components/ui/loader';
import { useSaleQuery } from '~/graphql/graphql.gen';
import { withAuthorizationRequired } from '~/libs/auth';
import { AppLoader } from '~/routes/_auth';
import { titleWrapper } from '~/utils/title';

export const SETUP_SCOPES = [
  'scope.meusnidus.sales.view',
  'scope.meusnidus.sales.write',
];

export const Route = createFileRoute('/_auth/setup/$saleId')({
  component: withAuthorizationRequired(RouteComponent, {
    onRedirecting: AppLoader,
    requireScope: SETUP_SCOPES,
  }),
});

function RouteComponent() {
  const { t } = useTranslation();
  const { saleId } = Route.useParams();
  const [step, setStep] = useAtom(stepState);
  const setCrawlingProducts = useSetAtom(crawlingProductsState);

  const { data: sale, isLoading } = useSaleQuery(
    { key: saleId },
    {
      retry: false,
      select: (result) => result.sale,
    },
  );

  if (isLoading) {
    return (
      <>
        <title>{titleWrapper(t('setup_page.url_title'))}</title>
        <main className="h-svh w-full">
          <div className="flex h-full flex-col items-center justify-center gap-2">
            <Loader className="size-8" />
          </div>
        </main>
      </>
    );
  }

  if (!sale) {
    return (
      <>
        <title>{titleWrapper(t('setup_page.url_title'))}</title>
        <div className="flex h-full flex-col">
          <div className="mx-auto flex flex-auto flex-col">
            <div className="flex h-full max-w-2.5xl items-center p-8">
              <div className="flex flex-col items-center space-y-3">
                <AlertCircleIcon className="size-4" />
                <p className="flex flex-col space-y-1 text-center">
                  <span className="font-semibold text-gray-700">
                    {t('setup_page.errors.sale_not_found')}
                  </span>
                  <span className="text-gray-500">
                    {t('setup_page.errors.sale_not_exists')}
                  </span>
                </p>
              </div>
            </div>
          </div>
        </div>
      </>
    );
  }

  const getVariants = (product: CrawlingProduct) => product._subRows ?? [];

  const resetStatus = () => {
    setCrawlingProducts((previous) => {
      if (typeof previous === 'undefined') {
        return undefined;
      }
      return {
        ...previous,
        products: previous.products.map((entry) => ({
          ...entry,
          _status: CrawlingProductStatus.NOT_STARTED,
          _code: undefined,
          _message: undefined,
          _subRows: getVariants(entry).map((subEntry) => {
            return {
              ...subEntry,
              _status: CrawlingProductStatus.NOT_STARTED,
              _code: undefined,
              _message: undefined,
            };
          }),
        })),
      };
    });
  };

  let componentToRender;
  switch (step) {
    case Steps.UPLOAD:
      componentToRender = (
        <UploadCSV
          onCancel={() => setStep(Steps.UPLOAD)}
          onConfirm={() => {
            resetStatus();
            setStep(Steps.CRAWLING);
          }}
          region={sale.storeId}
        />
      );
      break;
    case Steps.CRAWLING:
      componentToRender = (
        <EnrichmentProducts
          onCancel={() => setStep(Steps.UPLOAD)}
          onConfirm={() => {
            resetStatus();
            setStep(Steps.EDIT);
          }}
        />
      );
      break;
    case Steps.EDIT:
      componentToRender = (
        <Edit
          onConfirm={() => {
            resetStatus();
            setStep(Steps.CREATE);
          }}
        />
      );
      break;
    case Steps.CREATE:
      componentToRender = <CreateProducts region={sale.storeId} />;
      break;
    default:
      componentToRender = (
        <UploadCSV
          onConfirm={() => {
            resetStatus();
            setStep(Steps.CRAWLING);
          }}
          onCancel={() => setStep(Steps.UPLOAD)}
          region={sale.storeId}
        />
      );
  }

  return (
    <>
      <title>{titleWrapper(t('setup_page.url_title'))}</title>
      {componentToRender}
    </>
  );
}
